import React, { lazy, Suspense, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Divider, Collapse, Box, Badge, Alert, Fab, Paper, Tooltip,
  Dialog, DialogContent,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SchoolIcon from '@mui/icons-material/School';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HelpIcon from '@mui/icons-material/Help';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import BugReportIcon from '@mui/icons-material/BugReport';
import ApiIcon from '@mui/icons-material/Api';
import LabelIcon from '@mui/icons-material/Label';
import DomainIcon from '@mui/icons-material/Domain';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LinkIcon from '@mui/icons-material/Link';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ImageIcon from '@mui/icons-material/Image';
import LayersIcon from '@mui/icons-material/Layers';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SummarizeIcon from '@mui/icons-material/Summarize';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SecurityIcon from '@mui/icons-material/Security';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ScienceIcon from '@mui/icons-material/Science';
import GroupsIcon from '@mui/icons-material/Groups';
import BiotechIcon from '@mui/icons-material/Biotech';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import HistoryIcon from '@mui/icons-material/History';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GavelIcon from '@mui/icons-material/Gavel';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TodayIcon from '@mui/icons-material/Today';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SellIcon from '@mui/icons-material/Sell';
import ListAltIcon from '@mui/icons-material/ListAlt';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import UserMenu from './UserMenu';
import ShareButton from './ShareButton';
import useSoundPref from '../hooks/useSoundPref';
import { playChime } from '../utils/playChime';
import { NEEDS_TAGGING_EVENT } from '../utils/needsTagging';
import FeedbackDialog from './FeedbackDialog';
import { useAuth } from './AuthGate';
import { CHAT_PANEL_WIDTH } from './chatPanelWidth';
import GlobalSearch from './GlobalSearch';
import MobileNav from './MobileNav';
import SectionSubNav from './SectionSubNav';
import DevAreaBanner from './DevAreaBanner';
import useIsMobile from '../hooks/useIsMobile';
import { usePageTracking, trackFeature } from '../hooks/useAnalytics';
import useChangelog, { resetChangelogCache } from '../hooks/useChangelog';
import useBuildInfo from '../hooks/useBuildInfo';
import { resetTagsCache } from '../hooks/useTags';

export { trackFeature };

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const RELEASE_DATE = process.env.REACT_APP_RELEASE_DATE || '';
// The panel (and react-markdown with it) loads on first open; the 2 s callout prefetches it.
const loadChatPanel = () => import('./ChatPanel');
const ChatPanel = lazy(loadChatPanel);

const DEFAULT_LOGO = `${API_BASE_URL}/logo`;

const FAB_OFFSET_KEY = 'tec_fab_offset';
const FAB_DRAG_THRESHOLD_PX = 5;
// 24 bottom + help 40 + gap 8 + chat 56 + slack: lets the end of any page scroll clear of the floating buttons.
const FAB_RESERVE_PX = 144;

// Admin menu groups (plan 2216 item 10): the group holding the active route opens itself.
const ADMIN_GROUP_PATHS = [
  ['backend', ['/admin/images', '/admin/api-tools', '/admin/access', '/admin/bot-detection', '/admin/internal-domains', '/admin/influence-cutover', '/admin/salesforce-auth', '/admin/agent-impl', '/admin/feature-requests', '/admin/audit']],
  ['comms', ['/admin/notifications', '/admin/communications', '/admin/chat-activity', '/admin/email-archive', '/admin/resend-report', '/admin/email-retry', '/admin/inbound-email']],
  ['content', ['/admin/tags', '/admin/content', '/admin/domain-matches', '/admin/exclusions']],
];

export default function Layout() {
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [labOpen, setLabOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminGroupToggled, setAdminGroupToggled] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // A hard navigation (typed URL, page refresh) remounts Layout and would otherwise
  // silently close a chat session the user was mid-conversation in — restore whether
  // the panel was open (useChatAgent separately restores the conversation itself).
  const [chatOpen, setChatOpen] = useState(() => localStorage.getItem('tec_chat_panel_open') === 'true');
  useEffect(() => {
    localStorage.setItem('tec_chat_panel_open', String(chatOpen));
  }, [chatOpen]);
  const chatEverOpened = useRef(chatOpen);
  if (chatOpen) chatEverOpened.current = true;
  const [chatSeed, setChatSeed] = useState(null);
  const [chatCallout, setChatCallout] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);

  // The docked ChatPanel is a persistent Drawer, which defaults to covering the
  // full viewport height including this sticky AppBar/sub-nav header — its
  // combined height varies by page (SectionSubNav differs) and by viewport
  // (mobile drops a row), so it's measured live rather than hardcoded. Exposed
  // as a CSS var (not React state) since only ChatPanel.jsx's own styling
  // needs it — no re-render of Layout itself required.
  const headerRef = useRef(null);
  const fabRef = useRef(null);
  const fabDrag = useRef(null);
  const [fabOffset, setFabOffset] = useState(() => {
    try {
      const o = JSON.parse(localStorage.getItem(FAB_OFFSET_KEY));
      return { dx: Number(o.dx) || 0, dy: Number(o.dy) || 0 };
    } catch { return { dx: 0, dy: 0 }; }
  });

  // cand = wanted offset, cur = offset the element's current rect already includes.
  const clampFabOffset = (cand, cur) => {
    const el = fabRef.current;
    if (!el) return cand;
    const r = el.getBoundingClientRect();
    const top = headerRef.current?.offsetHeight || 0;
    const dx = Math.min(Math.max(cand.dx, cur.dx - r.left), cur.dx + (window.innerWidth - r.right));
    const dy = Math.min(Math.max(cand.dy, cur.dy - (r.top - top)), cur.dy + (window.innerHeight - r.bottom));
    return { dx, dy };
  };
  const saveFabOffset = (o) => {
    try { localStorage.setItem(FAB_OFFSET_KEY, JSON.stringify(o)); } catch { /* storage unavailable */ }
  };
  // Listen on window, not the stack: the pointer leaves the small stack as soon as it moves.
  const onFabPointerDown = (e) => {
    if (isMobile || e.button !== 0) return;
    const d = { x: e.clientX, y: e.clientY, start: fabOffset, moved: false, last: fabOffset };
    fabDrag.current = d;
    const move = (ev) => {
      const mx = ev.clientX - d.x;
      const my = ev.clientY - d.y;
      if (!d.moved && Math.hypot(mx, my) <= FAB_DRAG_THRESHOLD_PX) return;
      d.moved = true;
      d.last = clampFabOffset({ dx: d.start.dx + mx, dy: d.start.dy + my }, d.last);
      setFabOffset(d.last);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (d.moved) {
        saveFabOffset(d.last);
        d.suppressClick = true;
        setTimeout(() => { if (fabDrag.current === d) fabDrag.current = null; }, 0);
      } else {
        fabDrag.current = null;
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  const onFabClickCapture = (e) => {
    if (fabDrag.current?.suppressClick) { e.stopPropagation(); e.preventDefault(); }
  };
  const resetFabOffset = () => {
    setFabOffset({ dx: 0, dy: 0 });
    saveFabOffset({ dx: 0, dy: 0 });
  };

  // Keep the stack on-screen after a window resize or the chat panel docking/undocking.
  useEffect(() => {
    const fix = () => setFabOffset((o) => {
      if (isMobile) return o;
      const c = clampFabOffset(o, o);
      if (c.dx === o.dx && c.dy === o.dy) return o;
      saveFabOffset(c);
      return c;
    });
    const t = setTimeout(fix, 260);
    window.addEventListener('resize', fix);
    return () => { clearTimeout(t); window.removeEventListener('resize', fix); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen]);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setHeaderHeightVar = () => {
      document.documentElement.style.setProperty('--tec-header-height', `${el.offsetHeight}px`);
    };
    setHeaderHeightVar();
    const observer = new ResizeObserver(setHeaderHeightVar);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [whatsNewBadge, setWhatsNewBadge] = useState(false);
  const [botBadgeCount, setBotBadgeCount] = useState(0);
  const [needsTagging, setNeedsTagging] = useState(null);
  const [emailHealthy, setEmailHealthy] = useState(null);
  const emailHealthChecked = React.useRef(false);
  const liveBotCount = React.useRef(0);
  const popupWindowRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, canRead, hubUrl } = useAuth();
  usePageTracking(user?.username);
  const { muted: soundMuted, toggle: toggleSound } = useSoundPref();

  // Derive active date range from URL params (shared with ChatPanel)
  const chatDateRange = (() => {
    const start = searchParams.get('startDate') || searchParams.get('start') || null;
    const end = searchParams.get('endDate') || searchParams.get('end') || null;
    return { start, end };
  })();

  const showAdmin = canRead('admin.tags') || canRead('admin.content') || canRead('admin.images') || canRead('admin.api_tools') || canRead('admin.access');

  // Show chat callout on every page visit (sessionStorage resets when tab closes)
  useEffect(() => {
    if (sessionStorage.getItem('chatCalloutSeen')) return;
    const t = setTimeout(() => {
      setChatCallout(true);
      loadChatPanel();
      playChime();
      sessionStorage.setItem('chatCalloutSeen', '1');
    }, 2000);
    return () => clearTimeout(t);
  }, []);

  // Check for unseen What's New content on mount — shares useChangelog()'s cached fetch
  // with WhatsNew.jsx's full page view instead of independently re-fetching /changelog.
  const { entries: changelogEntries, loading: changelogLoading } = useChangelog();
  const { label: buildLabel } = useBuildInfo();
  useEffect(() => {
    if (changelogLoading) return;
    const lastSeen = localStorage.getItem('whatsNewLastSeen');
    if (changelogEntries.length > 0 && changelogEntries[0].version !== lastSeen) {
      setWhatsNewBadge(true);
    }
  }, [changelogEntries, changelogLoading]);

  // Check for new bot domains (compare live count to last seen, stored per visit to /admin/bot-detection)
  useEffect(() => {
    if (!canRead('admin.access')) return;
    fetch(`${API_BASE_URL}/admin/bot-detection/stats`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const liveBots = data.by_status?.bot ?? 0;
        liveBotCount.current = liveBots;
        const lastBots = parseInt(localStorage.getItem('lastBotCount') || '0', 10);
        if (liveBots > lastBots) setBotBadgeCount(liveBots - lastBots);
      })
      .catch(() => {});
  }, [canRead]);

  // Red badge on the Tag Admin entry while product families hold SKUs with no Product tag (price-list loader queue).
  useEffect(() => {
    if (!canRead('admin.tags')) return undefined;
    const onEvent = (e) => setNeedsTagging(e.detail || null);
    window.addEventListener(NEEDS_TAGGING_EVENT, onEvent);
    fetch(`${API_BASE_URL}/sku-mappings/needs-tagging`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.counts) setNeedsTagging(data.counts); })
      .catch(() => {});
    return () => window.removeEventListener(NEEDS_TAGGING_EVENT, onEvent);
  }, [canRead]);

  // Admin editors of the tag taxonomy / changelog: leaving them invalidates the process-wide
  // caches, which a full page load used to do implicitly.
  // A layout effect, so it runs before the destination page's own (passive) effects read the cache.
  const prevPath = useRef(location.pathname);
  useLayoutEffect(() => {
    const prev = prevPath.current;
    prevPath.current = location.pathname;
    if (prev === location.pathname) return;
    if (prev.startsWith('/admin/tags')) resetTagsCache();
    if (prev.startsWith('/admin/content')) resetChangelogCache();
  }, [location.pathname]);

  // Clear badge when user visits /whats-new
  useEffect(() => {
    if (location.pathname === '/whats-new') {
      setWhatsNewBadge(false);
    }
    if (location.pathname === '/admin/bot-detection') {
      setBotBadgeCount(0);
      localStorage.setItem('lastBotCount', String(liveBotCount.current));
    }
  }, [location.pathname]);

  // Check email health once per session when entering an admin page
  useEffect(() => {
    if (
      !location.pathname.startsWith('/admin') ||
      !canRead('admin.api_tools') ||
      emailHealthChecked.current
    ) return;
    emailHealthChecked.current = true;
    fetch(`${API_BASE_URL}/system/email-health`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { ok: false, detail: 'Request failed' })
      .then(data => setEmailHealthy(data.ok === true))
      .catch(() => setEmailHealthy(false));
  }, [location.pathname, canRead]);

  const toggleDrawer = (open) => () => setDrawerOpen(open);
  // Sidebar links are SPA navigations now (no document load), so reset the scroll a page load used to.
  const onNavClick = (e) => {
    setDrawerOpen(false);
    const sameRoute = e.currentTarget.getAttribute('href') === location.pathname;
    if (!sameRoute && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) window.scrollTo(0, 0);
  };

  const isActive = (path) => location.pathname === path;
  const adminGroupOf = (pathname) => ADMIN_GROUP_PATHS.find(([, paths]) => paths.some((p) => pathname === p || pathname.startsWith(`${p}/`)))?.[0];
  const adminGroupOpen = (key) => (key in adminGroupToggled ? adminGroupToggled[key] : adminGroupOf(location.pathname) === key);
  const toggleAdminGroup = (key) => setAdminGroupToggled((t) => ({ ...t, [key]: !adminGroupOpen(key) }));
  const handleLogout = () => {
    window.location.href = `${API_BASE_URL}/auth/logout`;
  };

  const handleFeedback = () => {
    setDrawerOpen(false);
    setFeedbackOpen(true);
  };

  // Docked and popped-out chat are mutually exclusive — only one is ever visible.
  const handleOpenDocked = (source) => {
    if (popupWindowRef.current && !popupWindowRef.current.closed) {
      popupWindowRef.current.close();
      popupWindowRef.current = null;
    }
    setChatCallout(false);
    setChatOpen(true);
    trackFeature('chat_open', { source });
  };

  // "Create with Assistant" (events/2048 WS-B-4) and any future page-level entry point that
  // wants to open the docked chat pre-seeded with a prompt dispatches this window event —
  // ChatPanel is mounted here at the Layout level, not on the pages that want to open it.
  useEffect(() => {
    const handler = (e) => {
      setChatSeed({ text: e.detail?.initialInput || '', nonce: Date.now() });
      handleOpenDocked(e.detail?.source || 'seeded_prompt');
    };
    window.addEventListener('tec:open-chat-assistant', handler);
    return () => window.removeEventListener('tec:open-chat-assistant', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePopOut = (conversationId) => {
    const params = new URLSearchParams();
    if (conversationId) params.set('conversationId', String(conversationId));
    if (chatDateRange.start) params.set('startDate', chatDateRange.start);
    if (chatDateRange.end) params.set('endDate', chatDateRange.end);
    // Neither `noopener` nor `noreferrer` in the features string — per the HTML
    // spec `noreferrer` implies `noopener`, and either makes window.open return
    // null, losing the handle handleOpenDocked needs to close this window again.
    // Same-origin trusted route, so the opener link is harmless.
    const query = params.toString();
    popupWindowRef.current = window.open(
      query ? `/chat/popup?${query}` : '/chat/popup',
      'tec-chat-popup',
      'width=460,height=780',
    );
    setChatOpen(false);
  };

  const handleLogoUpload = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const { data } = await (await import('axios')).default.post(`${API_BASE_URL}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (data.ok) setLogoUrl(`${API_BASE_URL}/logo?t=${Date.now()}`);
    } catch (err) {
      console.error('Logo upload failed:', err);
    }
  };

  const drawerContent = (
    <Box sx={{
      width: 260, bgcolor: '#312525', color: '#ccc', height: '100%',
      '& .MuiListItemIcon-root': { color: '#ccc' },
      '& .MuiListItemButton-root:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
      '& .MuiListItemButton-root.Mui-selected': { bgcolor: 'rgb(0,0,0)' },
      '& .MuiDivider-root': { borderColor: 'rgba(255,255,255,0.15)' },
      '& .MuiSvgIcon-root': { color: '#ccc' },
    }} role="presentation">
      <List>
        {/* Primary: My Workshops — lives on the Events site (plan 2124); a full navigation, not a route */}
        <ListItem disablePadding>
          <ListItemButton component="a" href={`${hubUrl.replace(/\/$/, '')}/my`} onClick={() => setDrawerOpen(false)}>
            <ListItemIcon><EventIcon /></ListItemIcon>
            <ListItemText primary="My Workshops" />
          </ListItemButton>
        </ListItem>

        {/* Primary: Executive Summary */}
        {canRead('dashboard') && (
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/" selected={isActive('/')} onClick={onNavClick}>
            <ListItemIcon><SummarizeIcon /></ListItemIcon>
            <ListItemText primary="Executive Summary" secondary="Total Program Influence" secondaryTypographyProps={{ sx: { fontSize: '0.65rem', color: '#aaa' } }} />
          </ListItemButton>
        </ListItem>
        )}

        {/* Primary: Deep Dive */}
        {canRead('exec') && (
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/exec/filtered-brief" selected={isActive('/exec/filtered-brief')} onClick={onNavClick}>
              <ListItemIcon><FilterAltIcon /></ListItemIcon>
              <ListItemText primary="Deep Dive" secondary="TEC Product Attribution" secondaryTypographyProps={{ sx: { fontSize: '0.65rem', color: '#aaa' } }} />
            </ListItemButton>
          </ListItem>
        )}

        {/* Primary: TEC influence scorecard (plan 2154) */}
        {canRead('exec') && (
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/exec/influence" selected={isActive('/exec/influence')} onClick={onNavClick}>
              <ListItemIcon><QueryStatsIcon /></ListItemIcon>
              <ListItemText primary="Influence Scorecard" secondary="Win rate, speed, size, volume" secondaryTypographyProps={{ sx: { fontSize: '0.65rem', color: '#aaa' } }} />
            </ListItemButton>
          </ListItem>
        )}

        {/* Primary: Workshops (central engagement table, plan 2136) */}
        {canRead('search') && (
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/workshops" selected={location.pathname === '/workshops' || location.pathname.startsWith('/workshops/')} onClick={onNavClick}>
              <ListItemIcon><SchoolIcon /></ListItemIcon>
              <ListItemText primary="Workshops" secondary="Engagement and reading depth" secondaryTypographyProps={{ sx: { fontSize: '0.65rem', color: '#aaa' } }} />
            </ListItemButton>
          </ListItem>
        )}

        {/* Primary: Product Market (plan 2216 item 9; left the Development / Lab group) */}
        {canRead('exec') && (
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/product-market" selected={isActive('/product-market')} onClick={onNavClick}>
              <ListItemIcon><StorefrontIcon /></ListItemIcon>
              <ListItemText primary="Product Market" secondary="Whole-market deals, not TEC impact" secondaryTypographyProps={{ sx: { fontSize: '0.65rem', color: '#aaa' } }} />
            </ListItemButton>
          </ListItem>
        )}

        {/* Primary: Exec Deck */}
        {canRead('exec') && (
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/exec/deck" selected={isActive('/exec/deck')} onClick={onNavClick}>
              <ListItemIcon><SlideshowIcon /></ListItemIcon>
              <ListItemText primary="Exec Deck" secondary="Slide narrative, live numbers" secondaryTypographyProps={{ sx: { fontSize: '0.65rem', color: '#aaa' } }} />
            </ListItemButton>
          </ListItem>
        )}

        <Divider />

        {/* Participants group */}
        {canRead('dashboard') && (
        <>
        <ListItem disablePadding>
          <ListItemButton onClick={() => setParticipantsOpen(!participantsOpen)}>
            <ListItemIcon><GroupsIcon /></ListItemIcon>
            <ListItemText primary="Participants" />
            {participantsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={participantsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/participants/all" selected={isActive('/participants/all')} onClick={onNavClick}>
              <ListItemIcon><PeopleAltIcon /></ListItemIcon>
              <ListItemText primary="All Entrants" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/participants/today" selected={isActive('/participants/today')} onClick={onNavClick}>
              <ListItemIcon><TodayIcon /></ListItemIcon>
              <ListItemText primary="Today's Entrants" />
            </ListItemButton>
            {canRead('exec') && (
              <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/participants/briefing" selected={isActive('/participants/briefing')} onClick={onNavClick}>
                <ListItemIcon><QueryStatsIcon /></ListItemIcon>
                <ListItemText primary="Participant Data" />
              </ListItemButton>
            )}
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/participants/tags" selected={isActive('/participants/tags')} onClick={onNavClick}>
              <ListItemIcon><SellIcon /></ListItemIcon>
              <ListItemText primary="Tag Analytics" />
            </ListItemButton>
          </List>
        </Collapse>
        </>
        )}

        {/* Events group — gated on events:read (2026-09-03: opened to any
            authenticated user, registrant's default role included; Audience Lab
            and Templates stay dashboard:read-tier since those pages' own outer
            App.js route guard is unchanged, Settings/Block List keep their own
            finer per-item gates below) */}
        {canRead('events') && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setEventsOpen(!eventsOpen)}>
                <ListItemIcon><CalendarMonthIcon /></ListItemIcon>
                <ListItemText primary="Events" />
                {eventsOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={eventsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events" selected={isActive('/events') || /^\/events\/\d+/.test(location.pathname)} onClick={onNavClick}>
                  <ListItemIcon><ListAltIcon /></ListItemIcon>
                  <ListItemText primary="All Events" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events/new" selected={isActive('/events/new')} onClick={onNavClick}>
                  <ListItemIcon><AddCircleOutlineIcon /></ListItemIcon>
                  <ListItemText primary="Create Event" />
                </ListItemButton>
                {canRead('events') && (
                  <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events/audience-lab" selected={isActive('/events/audience-lab')} onClick={onNavClick}>
                    <ListItemIcon><TravelExploreIcon /></ListItemIcon>
                    <ListItemText primary="Audience Lab" />
                  </ListItemButton>
                )}
                {canRead('events') && (
                  <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events/templates" selected={isActive('/events/templates')} onClick={onNavClick}>
                    <ListItemIcon><DescriptionIcon /></ListItemIcon>
                    <ListItemText primary="Templates" />
                  </ListItemButton>
                )}
                {(process.env.REACT_APP_AUTH_DISABLED === 'true' || user?.permissions?.events?.includes('admin')) && (
                  <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events/settings" selected={isActive('/events/settings')} onClick={onNavClick}>
                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                    <ListItemText primary="Settings" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events/blacklist" selected={isActive('/events/blacklist')} onClick={onNavClick}>
                    <ListItemIcon><GavelIcon /></ListItemIcon>
                    <ListItemText primary="Block List" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events/registration-health" selected={isActive('/events/registration-health')} onClick={onNavClick}>
                    <ListItemIcon><MonitorHeartIcon /></ListItemIcon>
                    <ListItemText primary="Registration Health" />
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </>
        )}

        {/* Development / Lab group */}
        {canRead('exec') && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setLabOpen(!labOpen)}>
                <ListItemIcon><BiotechIcon /></ListItemIcon>
                <ListItemText primary="Development / Lab" />
                {labOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={labOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/exec" selected={isActive('/exec')} onClick={onNavClick}>
                  <ListItemIcon><SummarizeIcon /></ListItemIcon>
                  <ListItemText primary="Exec Roll Up (Dev)" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/impact" selected={isActive('/impact')} onClick={onNavClick}>
                  <ListItemIcon><AssessmentIcon /></ListItemIcon>
                  <ListItemText primary="Impact (Dev)" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/platform-usage" selected={isActive('/platform-usage')} onClick={onNavClick}>
                  <ListItemIcon><DashboardIcon /></ListItemIcon>
                  <ListItemText primary="Platform Usage (Dev)" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events/xperts24" selected={isActive('/events/xperts24')} onClick={onNavClick}>
                  <ListItemIcon><EventIcon /></ListItemIcon>
                  <ListItemText primary="Xperts 24 (Dev)" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/events/xperts25" selected={isActive('/events/xperts25')} onClick={onNavClick}>
                  <ListItemIcon><EventIcon /></ListItemIcon>
                  <ListItemText primary="Xperts 25 (Dev)" />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        <Divider />

        {/* Admin */}
        {showAdmin && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setAdminOpen(!adminOpen)}>
                <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                <ListItemText primary="Admin" />
                {adminOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {(canRead('admin.images') || canRead('admin.api_tools') || canRead('admin.access')) && (
                  <>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => toggleAdminGroup('backend')} aria-expanded={adminGroupOpen('backend')}>
                      <Badge badgeContent={botBadgeCount || 0} color="error" max={99} sx={{ mr: 2 }} data-testid="admin-backend-badge">
                        <ListItemText primary="Backend" />
                      </Badge>
                      {adminGroupOpen('backend') ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={adminGroupOpen('backend')} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                {canRead('admin.images') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/images" selected={isActive('/admin/images')} onClick={onNavClick}>
                    <ListItemIcon><LayersIcon /></ListItemIcon>
                    <ListItemText primary="Container Images" />
                  </ListItemButton>
                )}
                {canRead('admin.api_tools') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/api-tools" selected={isActive('/admin/api-tools')} onClick={onNavClick}>
                    <ListItemIcon><IntegrationInstructionsIcon /></ListItemIcon>
                    <ListItemText primary="API Tools" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/access" selected={isActive('/admin/access')} onClick={onNavClick}>
                    <ListItemIcon><SecurityIcon /></ListItemIcon>
                    <ListItemText primary="Access Control" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/bot-detection" selected={isActive('/admin/bot-detection')} onClick={onNavClick}>
                    <ListItemIcon>
                      <Badge badgeContent={botBadgeCount || 0} color="error" max={99}>
                        <SmartToyIcon />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary="Bot Detection" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/internal-domains" selected={isActive('/admin/internal-domains')} onClick={onNavClick}>
                    <ListItemIcon><DomainIcon /></ListItemIcon>
                    <ListItemText primary="Internal Domains" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/influence-cutover" selected={isActive('/admin/influence-cutover')} onClick={onNavClick}>
                    <ListItemIcon><SwapHorizIcon /></ListItemIcon>
                    <ListItemText primary="Influence Cutover" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/salesforce-auth" selected={isActive('/admin/salesforce-auth')} onClick={onNavClick}>
                    <ListItemIcon><VpnKeyOutlinedIcon /></ListItemIcon>
                    <ListItemText primary="Salesforce Login" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/agent-impl" selected={isActive('/admin/agent-impl')} onClick={onNavClick}>
                    <ListItemIcon><BuildIcon /></ListItemIcon>
                    <ListItemText primary="Agent Impl" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/feature-requests" selected={isActive('/admin/feature-requests')} onClick={onNavClick}>
                    <ListItemIcon><AssignmentIcon /></ListItemIcon>
                    <ListItemText primary="Feature Requests" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/audit" selected={isActive('/admin/audit')} onClick={onNavClick}>
                    <ListItemIcon><HistoryIcon /></ListItemIcon>
                    <ListItemText primary="Audit Log" />
                  </ListItemButton>
                )}
                {canRead('admin.images') && (
                  <ListItemButton sx={{ pl: 6 }} component="label">
                    <ListItemIcon><ImageIcon /></ListItemIcon>
                    <ListItemText primary="Change Logo" />
                    <input type="file" hidden accept="image/*"
                      onChange={(e) => { if (e.target.files[0]) handleLogoUpload(e.target.files[0]); }} />
                  </ListItemButton>
                )}
                      </List>
                    </Collapse>
                  </>
                )}
                {(canRead('admin.access')) && (
                  <>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => toggleAdminGroup('comms')} aria-expanded={adminGroupOpen('comms')}>
                      <ListItemText primary="Comms" />
                      {adminGroupOpen('comms') ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={adminGroupOpen('comms')} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/notifications" selected={isActive('/admin/notifications')} onClick={onNavClick}>
                    <ListItemIcon><NotificationsNoneIcon /></ListItemIcon>
                    <ListItemText primary="Notifications" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/communications" selected={isActive('/admin/communications')} onClick={onNavClick}>
                    <ListItemIcon><ForumOutlinedIcon /></ListItemIcon>
                    <ListItemText primary="Communications" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/chat-activity" selected={isActive('/admin/chat-activity')} onClick={onNavClick}>
                    <ListItemIcon><ChatBubbleOutlineIcon /></ListItemIcon>
                    <ListItemText primary="Chat Activity" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/email-archive" selected={isActive('/admin/email-archive')} onClick={onNavClick}>
                    <ListItemIcon><MailOutlineIcon /></ListItemIcon>
                    <ListItemText primary="Email Archive" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/resend-report" selected={isActive('/admin/resend-report')} onClick={onNavClick}>
                    <ListItemIcon><MailOutlineIcon /></ListItemIcon>
                    <ListItemText primary="Resend Report" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/email-retry" selected={isActive('/admin/email-retry')} onClick={onNavClick}>
                    <ListItemIcon><MailOutlineIcon /></ListItemIcon>
                    <ListItemText primary="Email Retry" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/inbound-email" selected={isActive('/admin/inbound-email')} onClick={onNavClick}>
                    <ListItemIcon><MailOutlineIcon /></ListItemIcon>
                    <ListItemText primary="Inbound Email" />
                  </ListItemButton>
                )}
                      </List>
                    </Collapse>
                  </>
                )}
                {(canRead('admin.tags') || canRead('admin.content') || canRead('admin.access')) && (
                  <>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => toggleAdminGroup('content')} aria-expanded={adminGroupOpen('content')}>
                      <Badge badgeContent={needsTagging?.families || 0} color="error" max={99} sx={{ mr: 2 }} data-testid="admin-content-badge">
                        <ListItemText primary="Content" />
                      </Badge>
                      {adminGroupOpen('content') ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={adminGroupOpen('content')} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                {canRead('admin.tags') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/tags" selected={isActive('/admin/tags')} onClick={onNavClick}>
                    <ListItemIcon>
                      <Badge badgeContent={needsTagging?.families || 0} color="error" max={99} data-testid="tag-admin-needs-badge">
                        <LabelIcon />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary="Tag Admin" secondary={needsTagging?.families ? 'Needs tagging' : undefined}
                      secondaryTypographyProps={{ color: 'error', variant: 'caption' }} />
                  </ListItemButton>
                )}
                {canRead('admin.content') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/content" selected={isActive('/admin/content')} onClick={onNavClick}>
                    <ListItemIcon><EditNoteIcon /></ListItemIcon>
                    <ListItemText primary="Content Admin" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/domain-matches" selected={isActive('/admin/domain-matches')} onClick={onNavClick}>
                    <ListItemIcon><LinkIcon /></ListItemIcon>
                    <ListItemText primary="Domain Matches" />
                  </ListItemButton>
                )}
                {canRead('admin.access') && (
                  <ListItemButton sx={{ pl: 6 }} component={RouterLink} to="/admin/exclusions" selected={isActive('/admin/exclusions')} onClick={onNavClick}>
                    <ListItemIcon><PersonOffIcon /></ListItemIcon>
                    <ListItemText primary="Exclusions" />
                  </ListItemButton>
                )}
                      </List>
                    </Collapse>
                  </>
                )}
              </List>
            </Collapse>
          </>
        )}

        <Divider />

        {/* Help */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setHelpOpen(!helpOpen)}>
            <ListItemIcon><HelpIcon /></ListItemIcon>
            <ListItemText primary="Help" />
            {helpOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={helpOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {canRead('dashboard') && (
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/architecture" selected={isActive('/architecture')} onClick={onNavClick}>
              <ListItemIcon><LayersIcon /></ListItemIcon>
              <ListItemText primary="Architecture" />
            </ListItemButton>
            )}
            {canRead('dashboard') && (
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/howto" selected={isActive('/howto')} onClick={onNavClick}>
              <ListItemIcon><MenuBookIcon /></ListItemIcon>
              <ListItemText primary="How To" />
            </ListItemButton>
            )}
            {canRead('exec') && (
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/methodology" selected={isActive('/methodology')} onClick={onNavClick}>
              <ListItemIcon><ScienceIcon /></ListItemIcon>
              <ListItemText primary="Methodology" />
            </ListItemButton>
            )}
            {canRead('dashboard') && (
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/page-guidelines" selected={isActive('/page-guidelines')} onClick={onNavClick}>
              <ListItemIcon><AutoStoriesIcon /></ListItemIcon>
              <ListItemText primary="Page Guidelines" />
            </ListItemButton>
            )}
            {canRead('dashboard') && (
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/whats-new" selected={isActive('/whats-new')} onClick={onNavClick}>
              <ListItemIcon>
                <Badge color="error" variant="dot" invisible={!whatsNewBadge}>
                  <NewReleasesIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="What's New" />
            </ListItemButton>
            )}
            {canRead('dashboard') && (
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/about" selected={isActive('/about')} onClick={onNavClick}>
              <ListItemIcon><InfoIcon /></ListItemIcon>
              <ListItemText primary="About" />
            </ListItemButton>
            )}
            <ListItemButton sx={{ pl: 4 }} component="a" href={`${API_BASE_URL}/docs`} target="_blank" rel="noopener noreferrer">
              <ListItemIcon><ApiIcon /></ListItemIcon>
              <ListItemText primary="API Docs" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={handleFeedback}>
              <ListItemIcon><BugReportIcon /></ListItemIcon>
              <ListItemText primary="Submit Bug / Feature Request" />
            </ListItemButton>
          </List>
        </Collapse>

        <Divider />

        {/* Logout */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <div className="App">
      <Box ref={headerRef} sx={{ position: 'sticky', top: 0, zIndex: 1100 }}>
      <AppBar position="static" sx={{ backgroundColor: 'rgb(218,41,28)' }}>
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Logo — spans full banner height */}
          <img
            src={logoUrl}
            alt="Logo"
            style={{ height: isMobile ? 40 : 72, padding: isMobile ? '4px 4px 4px 8px' : '6px 8px 6px 16px', cursor: 'pointer', objectFit: 'contain' }}
            onClick={() => navigate('/')}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {/* Right side: both text rows */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
            <Toolbar variant="dense" disableGutters style={{ minHeight: 'auto', padding: '4px 16px 0 0' }}>
              <Typography
                variant="h5"
                component="div"
                sx={{ flexGrow: 0, color: 'white', marginRight: 'auto', cursor: 'pointer' }}
                onClick={() => navigate('/')}
              >
                TEC Analytics
              </Typography>
              {!isMobile && <GlobalSearch />}
              <Tooltip title={soundMuted ? 'Sound off — click to unmute' : 'Sound on — click to mute'}>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={toggleSound}
                  sx={{ mr: 1 }}
                  aria-label={soundMuted ? 'Unmute sound' : 'Mute sound'}
                  aria-pressed={soundMuted}
                >
                  {soundMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
              </Tooltip>
              <ShareButton />
              <UserMenu />
              {!isMobile && (
                <IconButton
                  color="inherit"
                  onClick={toggleDrawer(true)}
                  sx={{ ml: 1 }}
                  aria-label="navigation menu"
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
            {!isMobile && (
              <div style={{ padding: '0 16px 4px 0', display: 'flex', justifyContent: 'space-between', color: 'white' }}>
                <div>
                  <span
                    onClick={() => navigate('/about')}
                    style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                    title="About / revision history"
                  >{buildLabel}</span> &nbsp; {RELEASE_DATE}
                </div>
                <div style={{
                  fontSize: "12px",
                  textAlign: 'right',
                }}>
                  Aggregate data is updated daily at midnight PST. User information is updated daily at 1 PM PST
                </div>
              </div>
            )}
          </Box>
        </Box>
      </AppBar>
      <SectionSubNav />
      </Box>

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}
        PaperProps={{ sx: { bgcolor: '#312525' } }}>
        {drawerContent}
      </Drawer>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      <Dialog fullScreen open={mobileSearchOpen} onClose={() => setMobileSearchOpen(false)}>
        <DialogContent sx={{ pt: 3 }}>
          <GlobalSearch variant="fullscreen" onNavigate={() => setMobileSearchOpen(false)} />
        </DialogContent>
      </Dialog>

      {chatEverOpened.current && (
        <Suspense fallback={null}>
          <ChatPanel
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            onPopOut={handlePopOut}
            dateRange={chatDateRange}
            chatSeed={chatSeed}
            page={location.pathname}
          />
        </Suspense>
      )}

      {/* Floating action buttons — bottom right (hidden on mobile to avoid bottom nav overlap) */}
      {(() => {
        const howtoTarget = (() => {
          const map = [
            // More specific /exec paths must precede the bare '/exec' prefix row below.
            ['/exec/deck',           '/howto#section-exec-deck'],
            ['/exec/filtered-brief', '/howto#section-deep-dive'],
            ['/exec',                '/howto#section-executive-summary'],
            ['/events/registration-health', '/howto#entry-641'],
            ['/events/blacklist',    '/howto#entry-627'],
            ['/events',              '/howto#section-events'],
            ['/impact',              '/howto#section-impact'],
            ['/insights',            '/howto#section-insights'],
            ['/admin/tags',          '/howto#section-tag-admin'],
            ['/howto',               null],
          ];
          for (const [prefix, anchor] of map) {
            if (location.pathname.startsWith(prefix)) return anchor;
          }
          return '/howto';
        })();
        return (
      <Box
        ref={fabRef}
        onPointerDown={onFabPointerDown}
        onClickCapture={onFabClickCapture}
        sx={{
        transform: isMobile ? 'none' : `translate(${fabOffset.dx}px, ${fabOffset.dy}px)`,
        userSelect: 'none',
        touchAction: isMobile ? 'auto' : 'none',
        position: 'fixed', bottom: isMobile ? 72 : 24,
        right: isMobile ? 16 : (chatOpen ? CHAT_PANEL_WIDTH + 24 : 24),
        transition: 'right 225ms cubic-bezier(0, 0, 0.2, 1)',
        zIndex: 1200, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1,
      }}>
        {!isMobile && (
          <Tooltip title="Drag to move these buttons; double-click to reset" placement="left">
            <DragIndicatorIcon
              data-testid="fab-grip"
              onDoubleClick={resetFabOffset}
              sx={{ transform: 'rotate(90deg)', fontSize: 18, color: 'text.disabled', cursor: 'grab', opacity: 0.6, '&:hover': { opacity: 1 } }}
            />
          </Tooltip>
        )}
        <Tooltip title="How-To Guide" placement="left">
          <Fab
            size="small"
            aria-label="Open How-To guide"
            onClick={() => howtoTarget && navigate(howtoTarget)}
            sx={{ bgcolor: '#444', color: 'white', '&:hover': { bgcolor: '#222' }, display: howtoTarget === null ? 'none' : 'flex' }}
          >
            <HelpIcon fontSize="small" />
          </Fab>
        </Tooltip>
        {chatCallout && (
          <Paper
            elevation={6}
            onClick={() => handleOpenDocked('callout')}
            sx={{
              px: 2, py: 1.5, maxWidth: 240, cursor: 'pointer',
              bgcolor: '#1a1a1a', border: '2px solid rgb(218,41,28)', borderRadius: 2,
              color: '#e8e8e8', fontSize: '0.85rem', lineHeight: 1.4,
              animation: 'chatCalloutIn 0.35s ease-out',
              '@keyframes chatCalloutIn': {
                from: { opacity: 0, transform: 'translateY(12px) scale(0.92)' },
                to:   { opacity: 1, transform: 'translateY(0) scale(1)' },
              },
              '&:hover': { borderColor: 'rgb(255,80,60)' },
              // speech bubble tail pointing down-right
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                right: 20,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '10px solid rgb(218,41,28)',
              },
              position: 'relative',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgb(218,41,28)', mb: 0.5 }}>
              New: AI Analytics Assistant
            </Typography>
            Ask questions about pipeline, win rates, and workshop effectiveness in plain English, or add new data points and features.
          </Paper>
        )}
        <Fab
          aria-label="Open TEC Analytics chat assistant"
          onClick={() => handleOpenDocked('fab')}
          sx={{
            bgcolor: 'rgb(218,41,28)',
            color: 'white',
            '&:hover': { bgcolor: 'rgb(180,30,20)' },
            ...(chatCallout && {
              animation: 'chatFabPulse 1.2s ease-in-out infinite',
              '@keyframes chatFabPulse': {
                '0%, 100%': { boxShadow: '0 0 0 0 rgba(218,41,28,0.7)' },
                '50%':       { boxShadow: '0 0 0 12px rgba(218,41,28,0)' },
              },
            }),
          }}
        >
          <ChatBubbleOutlineIcon />
        </Fab>
      </Box>
        );
      })()}

      {emailHealthy === false && location.pathname.startsWith('/admin') && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Email is not configured or unreachable — alert notifications and feedback submissions will not be delivered. Check SMTP settings in GCP Secret Manager.
        </Alert>
      )}

      {/* Docked chat shrinks the content area rather than overlaying it.
          The Drawer paper is position: fixed, so the offset has to be hand-wired. */}
      <Box
        sx={{
          paddingBottom: isMobile ? '64px' : `${FAB_RESERVE_PX}px`,
          marginRight: chatOpen && !isMobile ? `${CHAT_PANEL_WIDTH}px` : 0,
          transition: 'margin-right 225ms cubic-bezier(0, 0, 0.2, 1)',
        }}
      >
        <DevAreaBanner />
        <Outlet />
      </Box>

      {isMobile && <MobileNav onMoreClick={toggleDrawer(true)} onSearchClick={() => setMobileSearchOpen(true)} />}
    </div>
  );
}
